import { Sparkle } from "lucide-react";
import { formatNumber } from "@/lib/format-datetime";
import { cn } from "@/lib/utils";

type Translator = (key: string, values?: Record<string, string | number>) => string;

/**
 * `EX-PLR01`: the Solved / Tried column of every Judge list (`docs/03` §6).
 * "69 / 81" is people who got Accepted over people who sent a judged program,
 * in practice. The server's ten rule decides what shows (ADR-0081): under ten
 * solvers the solved side reads "<10", and under ten triers the cell is "New",
 * so nobody can tell one solver from none. The bar is the share of triers who
 * solved it; the hover card adds the submissions.
 */
export type SolveCountLabels = {
  under: string;
  fresh: string;
  fewSolves: string;
  solvedBy: string;
  triedBy: string;
  submissions: string;
  people: (n: number) => string;
  /** The cell read aloud, whole: the numbers are drawn for the eye only. */
  aloud: (solvers: number | null, attempters: number) => string;
  aloudFresh: string;
};

/** The labels from the `judge.solveCount` messages, in the reader's digits. */
export function solveCountLabels(t: Translator, locale: string): SolveCountLabels {
  const n = (value: number) => formatNumber(value, locale);
  return {
    under: t("under"),
    fresh: t("fresh"),
    fewSolves: t("fewSolves"),
    solvedBy: t("solvedBy"),
    triedBy: t("triedBy"),
    submissions: t("submissions"),
    people: (value) => t("people", { n: value, count: n(value) }),
    aloud: (solvers, attempters) =>
      solvers === null
        ? t("aloudFew", { tried: n(attempters) })
        : t("aloud", { solved: n(solvers), tried: n(attempters) }),
    aloudFresh: t("aloudFresh"),
  };
}

export type SolveCountLayout = "stack" | "inline" | "plain";

export function SolveCount({
  solvers,
  attempters,
  submissions,
  locale,
  labels,
  layout = "stack",
  className,
}: {
  solvers: number | null | undefined;
  attempters: number | null | undefined;
  submissions: number | null | undefined;
  locale: string;
  labels: SolveCountLabels;
  /** `stack` the library's cell, `inline` the Problems set's, `plain` the number alone. */
  layout?: SolveCountLayout;
  className?: string;
}) {
  const n = (value: number) => formatNumber(value, locale);

  if (attempters === null || attempters === undefined) {
    return (
      <span className={cn("inline-flex", className)}>
        <span className="inline-flex h-[22px] items-center gap-1.5 rounded-md bg-primary/10 px-2 text-xs font-bold text-primary-ink">
          <Sparkle className="h-3 w-3" strokeWidth={2.4} aria-hidden />
          {labels.fresh}
        </span>
        <span className="sr-only">{labels.aloudFresh}</span>
      </span>
    );
  }

  const solved = solvers ?? null;
  const share = solved === null ? null : Math.round((solved / attempters) * 100);
  const figure = (
    <span className="whitespace-nowrap text-[14.5px] font-bold tabular-nums text-neutral-900">
      {solved === null ? labels.under : n(solved)}
      <span className="font-medium text-neutral-500"> / {n(attempters)}</span>
    </span>
  );
  const bar =
    share === null ? null : (
      <span className="block h-1 w-14 shrink-0 overflow-hidden rounded-full bg-neutral-100">
        <span className="block h-full rounded-full bg-primary" style={{ width: `${share}%` }} />
      </span>
    );
  const card =
    submissions === null || submissions === undefined ? null : (
      <span className="pointer-events-none absolute right-0 top-full z-20 mt-2 hidden w-[212px] flex-col gap-1.5 rounded-[10px] bg-neutral-900 px-3.5 py-3 text-[13px] text-white shadow-[0_8px_30px_rgba(0,0,0,0.18)] group-hover/count:flex">
        {solved === null ? null : (
          <span className="flex justify-between gap-3">
            <span className="text-neutral-300">{labels.solvedBy}</span>
            <b className="tabular-nums">{labels.people(solved)}</b>
          </span>
        )}
        <span className="flex justify-between gap-3">
          <span className="text-neutral-300">{labels.triedBy}</span>
          <b className="tabular-nums">{labels.people(attempters)}</b>
        </span>
        <span className="flex justify-between gap-3">
          <span className="text-neutral-300">{labels.submissions}</span>
          <b className="tabular-nums">{n(submissions)}</b>
        </span>
      </span>
    );

  return (
    <span className={cn("group/count relative inline-flex", className)}>
      <span className="sr-only">{labels.aloud(solved, attempters)}</span>
      <span aria-hidden className="contents">
        {layout === "inline" ? (
          <span className="inline-flex items-center gap-3">
            {bar}
            {figure}
          </span>
        ) : layout === "plain" ? (
          figure
        ) : (
          <span className="inline-flex flex-col items-end gap-1.5">
            {figure}
            {share === null ? (
              <span className="text-xs text-neutral-500">{labels.fewSolves}</span>
            ) : (
              <span className="hidden items-center gap-2 lg:inline-flex">
                {bar}
                <span className="min-w-[30px] text-right text-xs tabular-nums text-neutral-500">{n(share)}%</span>
              </span>
            )}
          </span>
        )}
        {card}
      </span>
    </span>
  );
}
